<template>
  <div>
    <h2 :class="['headline', 'font-grey', 'font-l-600']" v-html="title" />
    <div :class="['server-side-description']" v-html="description" />
    <example-title src="example-1.html" title="Card with vc-collapsible, vc-list and vc-rating" />
    <vc-layout :spacing="8">
      <vc-col :span="8" v-for="(guitar, index) in guitars" :key="index" sm12 md12 xs24>
        <vc-card>
          <vc-card-media height="216px" :background="guitar.thumb" />
          <vc-card-title>
            <h3 :class="['subheading', 'ma-0', 'pa-0', 'ellipsis', 'font-indigo', 'align-center']" v-html="guitar.title" />
          </vc-card-title>
          <vc-card-text>
            <vc-collapsible color="indigo" :name="guitar.title" :multiple="false">
              <vc-collapsible-item has-vc-list max-height="186px" label="Details" icon="mdi-settings">
                <vc-list>
                  <vc-list-item v-for="(value, key) in guitar.details" :key="key">
                    <vc-list-item-primary-text>{{ key }}</vc-list-item-primary-text>
                    <vc-list-item-secondary-text>{{ value }}</vc-list-item-secondary-text>
                  </vc-list-item>
                </vc-list>
              </vc-collapsible-item>
              <vc-collapsible-item has-vc-list label="Rating" icon="mdi-star">
                <div :class="['align-center']">
                  <vc-rating :class="['my-8']" v-model="rating" size="normal" />
                </div>
              </vc-collapsible-item>
            </vc-collapsible>
          </vc-card-text>
          <vc-card-actions>
            <vc-button block color="indigo" tag="a" :to="guitar.url" target="_blank" icon-left="camera">Photos</vc-button>
          </vc-card-actions>
        </vc-card>
      </vc-col>
    </vc-layout>
    <example-title src="example-2.html" title="Cards as HTML links, without action buttons and media color" />
    <vc-layout :spacing="8">
      <vc-col :span="8" sm24>
        <vc-card tag="a" :href="searchAction('winter')" target="_blank">
          <vc-card-media tag="span" height="52px" color="black">
            <h2 :class="['title', 'font-l-600', 'align-center', 'font-white']">Winter Photos</h2>
          </vc-card-media>
          <vc-card-title tag="span">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis']">Winter</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
      <vc-col :span="8" sm24>
        <vc-card tag="a" :href="searchAction('sky')" target="_blank">
          <vc-card-media tag="span" height="52px" color="blue">
            <h2 :class="['title', 'font-l-600', 'align-center', 'font-white']">Sky Photos</h2>
          </vc-card-media>
          <vc-card-title tag="span">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis']">Sky</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
      <vc-col :span="8" sm24>
        <vc-card tag="a" :href="searchAction('woods')" target="_blank">
          <vc-card-media tag="span" height="52px" color="light-green">
            <h2 :class="['title', 'font-l-600', 'align-center', 'font-white']">Woods Photos</h2>
          </vc-card-media>
          <vc-card-title tag="span">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis']">Woods</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
    </vc-layout>
    <example-title src="example-3.html" title="Cards as Vue router-links, without media and action buttons" />
    <vc-layout :spacing="8">
      <vc-col :span="8" sm24>
        <vc-card tag="router-link" href="/components">
          <vc-card-title tag="span" color="indigo" :dark="false">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis', 'align-center']">Components</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
      <vc-col :span="8" sm24>
        <vc-card tag="router-link" href="/style">
          <vc-card-title tag="span" color="teal" :dark="false">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis', 'align-center']">Styles and Colors</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
      <vc-col :span="8" sm24>
        <vc-card tag="router-link" href="/layout">
          <vc-card-title tag="span" color="pink" :dark="false">
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis', 'align-center']">Layout</h3>
          </vc-card-title>
          <vc-card-text tag="span">{{ cardText }}</vc-card-text>
        </vc-card>
      </vc-col>
    </vc-layout>
    <example-title src="example-4.html" title="Parallax card" />
    <vc-layout :spacing="8">
      <vc-col :span="24">
        <vc-card>
          <vc-card-media height="200px" parallax background="/static/images/stormy-weather-at-freshwater-bay.jpg">
            <h2 :class="['display-1', 'font-l-600', 'align-center', 'font-white']" style="padding-top: 56px;" v-html="scrollToObserve" />
          </vc-card-media>
          <vc-card-title>
            <h3 :class="['headline', 'ma-0', 'pa-0', 'ellipsis']" v-html="scrollToObserve" />
          </vc-card-title>
          <vc-card-text>
            <vc-lorem-ipsum :repeats="3" />
          </vc-card-text>
          <vc-card-actions>
            <vc-button color="warning" :class="['ml-0']">Button</vc-button>
            <vc-button color="pink" :class="['ml-0']">Button</vc-button>
          </vc-card-actions>
        </vc-card>
      </vc-col>
    </vc-layout>
    <api-grid ref="api-grid" />
  </div>
</template>
<script>
import methods from '../helpers/methods'
import guitars from '../../static/data/guitars'

export default {
  name: 'CardPage',
  mixins: [
    methods
  ],
  data () {
    return {
      title: null,
      description: null,
      guitars: null,
      rating: 8,
      searchAction: (q) => `https://www.google.com/search?q=${q}`,
      scrollToObserve: 'Scroll the page to observe the parallax effect',
      cardText: 'Ut enim ad minima veniam, quis nostrum exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur?'
    }
  },
  mounted () {
    this.getGuitarsData()
  },
  methods: {
    getGuitarsData () {
      const promises = []
      const cards = []
      const guitarsList = guitars.guitars
      const root = 'https://wemakesites.net/'
      this.$root.$emit(this.$store.state.events.ajaxStart)
      guitarsList.forEach(guitar => {
        const url = `${root}api/data/gear/${guitar}`
        promises.push(this.$http(url))
      })
      this.$http
        .all(promises)
        .then(this.$http.spread((...response) => {
          response.forEach((item, index) => {
            const slug = guitarsList[index]
            const title = slug.replace(/-/g, ' ').toUpperCase()
            cards.push({
              slug,
              title,
              details: item.data.data,
              thumb: `${root}images/music/${slug}/image-01.jpg`,
              url: `${root}music/the-guitars/${slug}?caption=${encodeURIComponent(title)}&description=`
            })
          })
          this.guitars = cards
          this.getPage()
        }))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/card.vue